import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank());

  function blank() { return { name: "", client_id: "", monthly_price: 0, start_date: "", end_date: "", benefits: "", active: true }; }

  const load = () => { setLoading(true); api.get("/subscriptions").then((r) => setSubs(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); }, []);

  const openNew = () => { setEdit(null); setForm(blank()); setShowForm(true); };
  const openEdit = (s) => { setEdit(s); setForm({ ...blank(), ...s }); setShowForm(true); };

  const save = async () => {
    try {
      const payload = { ...form, monthly_price: Number(form.monthly_price) || 0, client_id: form.client_id || null };
      if (edit) await api.put(`/subscriptions/${edit.id}`, payload);
      else await api.post("/subscriptions", payload);
      toast.success("Abonnement enregistré");
      setShowForm(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggle = async (s) => { await api.post(`/subscriptions/${s.id}/toggle`); load(); };
  const isExpired = (s) => s.end_date && new Date(s.end_date) < new Date();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Abonnements</h1><p className="text-slate-500 text-sm mt-1">Formules et avantages clients</p></div>
        <Button data-testid="subs-new-btn" onClick={openNew} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-2" /> Nouvel abonnement</Button>
      </div>

      {loading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {subs.map((s) => (
            <div key={s.id} data-testid={`sub-card-${s.id}`} className="bg-white border rounded-2xl p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center"><CreditCard className="w-5 h-5" /></div><div className="font-semibold">{s.name}</div></div>
                <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${isExpired(s) ? "bg-slate-100 text-slate-500 border-slate-200" : s.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{isExpired(s) ? "Expiré" : s.active ? "Actif" : "Inactif"}</span>
              </div>
              <div className="text-sm text-slate-500 mt-2 space-y-0.5">
                <div>Client : {s.client_name || "—"}</div>
                <div>Prix mensuel : <b className="text-slate-800">{money(s.monthly_price)}</b></div>
                <div>Du {fmtDate(s.start_date)} au {fmtDate(s.end_date)}</div>
                {s.benefits && <div className="text-xs">{s.benefits}</div>}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <label className="flex items-center gap-2 text-sm"><Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`sub-toggle-${s.id}`} /> {s.active ? "Actif" : "Inactif"}</label>
                <Button variant="outline" size="sm" onClick={() => openEdit(s)}>Modifier</Button>
              </div>
            </div>
          ))}
          {subs.length === 0 && <p className="text-slate-400 col-span-full text-center py-10">Aucun abonnement</p>}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Modifier" : "Nouvel"} abonnement</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Nom *</Label><Input data-testid="sf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Prix mensuel ($)</Label><Input data-testid="sf-price" type="number" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} className="mt-1" /></div>
            <div><Label>Date début</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" /></div>
            <div><Label>Date expiration</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Avantages</Label><Textarea value={form.benefits || ""} onChange={(e) => setForm({ ...form, benefits: e.target.value })} className="mt-1" rows={2} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button data-testid="sf-submit" onClick={save} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
